import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef } from 'react';

import { importWatchlist, watchlistQueries } from '@/api';
import { useSession } from '@/hooks/use-session';

import { readGuestWatchlist, useGuestWatchlistIds, writeGuestWatchlist } from './use-watchlist';

/**
 * Переносить гостьовий Watchlist у акаунт одразу після входу.
 *
 * Лоти, що вже закрились (`notFound`), перенести неможливо — копію зняти
 * нічим, і з браузера вони йдуть. Ті, що не вдались через зайнятого
 * провайдера (`failed`), лишаються в браузері й поїдуть наступного разу.
 * Нічого не малює: це синхронізація з зовнішньою системою, тобто рівно те,
 * для чого існує ефект. Спроба одна на вхід — інакше лот, який провайдер
 * зараз не віддає, крутив би запити по колу.
 */
export function WatchlistSync(): ReactNode {
	const { user } = useSession();
	const guestIds = useGuestWatchlistIds();
	const queryClient = useQueryClient();
	const attemptedFor = useRef<string | null>(null);

	useEffect(() => {
		if (!user) {
			attemptedFor.current = null;
			return;
		}
		if (guestIds.length === 0 || attemptedFor.current === user.id) return;
		attemptedFor.current = user.id;
		const ids = readGuestWatchlist();

		importWatchlist({ vehicleIds: ids })
			.then(async (result) => {
				writeGuestWatchlist(result.failed);
				await queryClient.invalidateQueries({ queryKey: watchlistQueries.all() });
			})
			// Мережа впала — гостьовий список лишається як був і спробує знову при наступному вході.
			.catch(() => undefined);
	}, [user, guestIds, queryClient]);

	return null;
}
